import {
	Button,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Calendar
} from '@/components/ui';
import { ButtonFilterOptions } from '@/components/custom';
import { useQueryParams } from '@/hooks';
import type { ISelectOption } from '@/interfaces';
import { AuditLogActionEnum, AuditLogEntityTypeEnum } from '@/enums';
import { cn } from '@/lib/utils';
import { format, startOfDay, endOfDay, startOfMonth, endOfMonth, subMonths, addMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';

const ACTION_OPTIONS: ISelectOption[] = [
	{ title: 'Créé', value: AuditLogActionEnum.CREATE },
	{ title: 'Modifié', value: AuditLogActionEnum.UPDATE },
	{ title: 'Supprimé', value: AuditLogActionEnum.DELETE }
];

const ENTITY_TYPE_OPTIONS: ISelectOption[] = [
	{ title: 'Transaction comptable', value: AuditLogEntityTypeEnum.ACCOUNTING_TRANSACTION }
];

interface AuditFiltersProps {
	isLoading: boolean;
}

const AuditFilters = ({ isLoading }: AuditFiltersProps) => {
	const { updateSearchParams, urlSearchParams, values: [from, to], setValues } = useQueryParams(['from', 'to']);

	const dateRange = {
		from: from ? new Date(from as string) : startOfMonth(new Date()),
		to: to ? new Date(to as string) : endOfMonth(new Date())
	};

	const handleDateRangeChange = (range: { from?: Date; to?: Date }) => {
		if (range.from && range.to) {
			setValues({
				from: startOfDay(range.from).toISOString(),
				to: endOfDay(range.to).toISOString(),
				page: '1'
			});
		}
	};

	const handleQuickPeriod = (period: 'current' | 'previous' | 'next') => {
		const ref = dateRange.from;
		const map = {
			previous: [startOfMonth(subMonths(ref, 1)), endOfMonth(subMonths(ref, 1))],
			next: [startOfMonth(addMonths(ref, 1)), endOfMonth(addMonths(ref, 1))],
			current: [startOfMonth(new Date()), endOfMonth(new Date())]
		} as const;
		const [f, t] = map[period];
		setValues({ from: f.toISOString(), to: t.toISOString(), page: '1' });
	};

	const handleOnChangeFilter = (key: string, options: ISelectOption[]) =>
		updateSearchParams(params => {
			params.getAll(key).forEach(v => params.delete(key, v));
			for (const o of options) params.append(key, o.value as string);
			params.set('page', '1');
			return params;
		});

	return (
		<div className="flex flex-wrap items-center gap-2">
			<ButtonFilterOptions
				disabled={isLoading}
				options={ACTION_OPTIONS}
				onChange={options => handleOnChangeFilter('action', options)}
				value={urlSearchParams.getAll('action')}
				text="Action"
			/>
			<ButtonFilterOptions
				disabled={isLoading}
				options={ENTITY_TYPE_OPTIONS}
				onChange={options => handleOnChangeFilter('entityType', options)}
				value={urlSearchParams.getAll('entityType')}
				text="Type d'entité"
			/>

			<div className="flex items-center gap-1">
				<Button
					variant="outline"
					size="sm"
					className="h-9 px-2"
					disabled={isLoading}
					onClick={() => handleQuickPeriod('previous')}
				>
					<ChevronLeft className="h-4 w-4" />
				</Button>

				<Popover>
					<PopoverTrigger asChild>
						<Button
							variant="outline"
							className={cn('h-9 w-[245px] justify-start text-left font-normal text-sm')}
							disabled={isLoading}
						>
							<CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
							<span>
								{format(dateRange.from, 'dd MMM', { locale: fr })} &mdash; {format(dateRange.to, 'dd MMM yyyy', { locale: fr })}
							</span>
						</Button>
					</PopoverTrigger>
					<PopoverContent className="w-auto p-0" align="start">
						<Calendar
							mode="range"
							defaultMonth={dateRange.from}
							selected={{ from: dateRange.from, to: dateRange.to }}
							onSelect={range => range && handleDateRangeChange(range)}
							numberOfMonths={2}
						/>
						<div className="flex gap-2 p-3 border-t">
							<Button
								variant="outline"
								size="sm"
								className="flex-1"
								onClick={() => handleQuickPeriod('current')}
							>
								Ce mois
							</Button>
							<Button
								variant="outline"
								size="sm"
								className="flex-1"
								onClick={() => handleQuickPeriod('previous')}
							>
								Mois préc.
							</Button>
						</div>
					</PopoverContent>
				</Popover>

				<Button
					variant="outline"
					size="sm"
					className="h-9 px-2"
					disabled={isLoading}
					onClick={() => handleQuickPeriod('next')}
				>
					<ChevronRight className="h-4 w-4" />
				</Button>
			</div>
		</div>
	);
};

export default AuditFilters;
